import { IOrder, IOrderItem } from "../model/IOrder";

// --
// -- The report columns the CRM does not return ready-made.
// --
// -- The old SQL view derived them with correlated sub-selects over
// -- order_items; ordermanagement/search already ships every order with its
// -- items attached, so the same figures are worked out here instead of asking
// -- the backend for a second, wider endpoint.
// --

const sum = (items: IOrderItem[], pick: (item: IOrderItem) => number | null | undefined): number | null => {
  const values = items
    .map(pick)
    .filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

  // -- SUM() over no rows is NULL in SQL, and the column renders blank rather
  // -- than a misleading $0.00. Keep that.
  return values.length
    ? values.reduce((acc, value) => acc + value, 0)
    : null;
};

// --
// -- orderItems arrives as a JSON array built from a Java HashSet, so it has no
// -- guaranteed order. The old view's "LIMIT 1" was equally arbitrary; sorting
// -- by id at least makes the chosen row stable between reloads.
// --
export const orderedItems = (order: IOrder): IOrderItem[] =>
  [...(order.orderItems || [])].sort((a, b) => (a?.id || 0) - (b?.id || 0));

export const firstItem = (order: IOrder): IOrderItem | undefined => orderedItems(order)[0];

/** `Supplier` — the vendor on the order's first item. */
export const supplierName = (order: IOrder): string =>
  firstItem(order)?.vendor?.name || '';

/** `PartName` */
export const partName = (order: IOrder): string => firstItem(order)?.name || '';

/** `Interchange` */
export const interchange = (order: IOrder): string => firstItem(order)?.interchangeNumber || '';

/** `Make` `Model` `Year`, as one cell. */
export const vehicle = (order: IOrder): string => {
  const item = firstItem(order);
  if (!item) return '';

  return [item.viYear, item.viMake, item.viModel]
    .filter((part) => !!part && String(part).trim())
    .join(' ');
};

/** `PartPrice` — SUM(order_items.raw_price) */
export const partPrice = (order: IOrder): number | null =>
  sum(orderedItems(order), (item) => item.rawPrice);

/** `vPrice` — SUM(order_items.v_price) */
export const vendorPrice = (order: IOrder): number | null =>
  sum(orderedItems(order), (item) => item.vPrice);

/** `fPrice` — SUM(order_items.feed_price) */
export const feedPrice = (order: IOrder): number | null =>
  sum(orderedItems(order), (item) => item.feedPrice);

/** `Fr8_Discount` — SUM(order_items.shipping_discount) */
export const freightDiscount = (order: IOrder): number | null =>
  sum(orderedItems(order), (item) => item.shippingDiscount);

// --
// -- SUM(order_items.raw_shipping). The by-date report's `Shipping` column is
// -- this, not orders.shipping - they are different numbers and the report
// -- means the per-item one.
// --
export const rawShipping = (order: IOrder): number | null =>
  sum(orderedItems(order), (item) => item.rawShipping);

/** SUM(order_items.qty) — the report's `Qty`, not orders.total_items. */
export const itemQty = (order: IOrder): number | null =>
  sum(orderedItems(order), (item) => item.qty);

// --
// -- `Gross$` — part price less vendor price less discount.
// --
// -- Blank when either sum is missing: with no vendor price this would read as
// -- the whole part price being profit, which is worse than showing nothing.
// --
export const grossAmount = (order: IOrder): number | null => {
  const parts = partPrice(order);
  const vendor = vendorPrice(order);

  return parts === null || vendor === null
    ? null
    : parts - vendor - (order.discountAmount || 0);
};

// --
// -- `Gross%` — gross as a share of the part price.
// --
// -- Null rather than zero when there is no part price to divide by: the old
// -- report divided anyway and printed NaN%.
// --
export const grossPercent = (parts: number | null, gross: number | null): number | null =>
  parts === null || gross === null || parts === 0
    ? null
    : (gross / parts) * 100;

/** `Subtotal` — subtotal_price - discount_amount */
export const subtotal = (order: IOrder): number | null =>
  order.subtotalPrice === null || order.subtotalPrice === undefined
    ? null
    : order.subtotalPrice - (order.discountAmount || 0);

/** `Margin` — subtotal_price - discount_amount - SUM(v_price) */
export const margin = (order: IOrder): number | null => {
  const net = subtotal(order);
  const vendor = vendorPrice(order);

  // -- Without a vendor price there is no margin to state; showing the subtotal
  // -- would read as 100% margin, which is worse than showing nothing.
  return net === null || vendor === null
    ? null
    : net - vendor;
};

const distinctJoined = (order: IOrder, pick: (item: IOrderItem) => string | null | undefined): string =>
  Array.from(new Set(
    orderedItems(order)
      .map(pick)
      .map((value) => (value || '').trim())
      .filter((value) => !!value)
  ))
    .sort()
    .join(', ');

/** `Invoices` — GROUP_CONCAT(DISTINCT supplier_invoice) */
export const invoices = (order: IOrder): string =>
  distinctJoined(order, (item) => item.supplierInvoice);

/** `SalesOrderNumber` — GROUP_CONCAT(DISTINCT sales_order_number) */
export const salesOrderNumbers = (order: IOrder): string =>
  distinctJoined(order, (item) => item.salesOrderNumber);
